/**
 * The strings, in the locale being read.
 *
 * `t('pricing.hero.title')` looks a key up in three places in order — the
 * locale's own override file (`en-us`), its language (`de`), then English —
 * and returns the key itself when none has it, so a missing string is visible
 * in review rather than an empty space in production. `{name}` placeholders
 * are filled from `vars`; an unknown placeholder is left as written.
 *
 * `tn(key, count)` picks `key.one` / `key.other` (and the language's other
 * plural categories) through `Intl.PluralRules`, with `count` available to the
 * string as `{count}`.
 *
 * English renders on the first paint whatever the locale; another language's
 * catalogue arrives a moment later and the tree re-renders with it. `ready`
 * says which state a page is in, for anything that wants to wait.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { localeOf, type Language } from '@shared/locales';
import { useRouter } from '../router';
import { EN, hasCatalogue, loadCatalogue } from './catalogue';
import { setIntlLocale } from './format';
import type { Catalogue, Vars } from './types';

export interface I18n {
  t: (key: string, vars?: Vars) => string;
  tn: (key: string, count: number, vars?: Vars) => string;
  /** False while a non-English catalogue is still in flight. */
  ready: boolean;
  language: Language;
  /** The BCP 47 tag `Intl` is given: `de-DE`. */
  intl: string;
  /** The locale tag: `de-de`. */
  locale: string;
}

const I18nContext = createContext<I18n | null>(null);

const EMPTY: Catalogue = {};

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
  );
}

interface Loaded {
  /** Which locale these tables are for. */
  tag: string;
  language: Catalogue;
  override: Catalogue;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { locale } = useRouter();
  const info = localeOf(locale);
  const needsLanguage = info.language !== 'en';
  const needsOverride = hasCatalogue(info.tag);

  const [loaded, setLoaded] = useState<Loaded>({ tag: '', language: EMPTY, override: EMPTY });

  useEffect(() => {
    if (!needsLanguage && !needsOverride) return;
    let cancelled = false;
    Promise.all([
      needsLanguage ? loadCatalogue(info.language) : Promise.resolve(EMPTY),
      needsOverride ? loadCatalogue(info.tag) : Promise.resolve(EMPTY)
    ]).then(([language, override]) => {
      if (!cancelled) setLoaded({ tag: info.tag, language, override });
    });
    return () => {
      cancelled = true;
    };
  }, [info.tag, info.language, needsLanguage, needsOverride]);

  const value = useMemo<I18n>(() => {
    // Before the children render, so formatDate and friends see the right tag
    // on the same pass. Idempotent, so the double render in StrictMode is fine.
    setIntlLocale(info.intl);

    const current = loaded.tag === info.tag;
    const language = current ? loaded.language : EMPTY;
    const override = current ? loaded.override : EMPTY;
    const lookup = (key: string): string | undefined => override[key] ?? language[key] ?? EN[key];

    const t: I18n['t'] = (key, vars) => {
      const text = lookup(key);
      return text === undefined ? key : interpolate(text, vars);
    };

    const rules = new Intl.PluralRules(info.intl);
    const tn: I18n['tn'] = (key, count, vars) => {
      const text = lookup(`${key}.${rules.select(count)}`) ?? lookup(`${key}.other`);
      return text === undefined ? key : interpolate(text, { count, ...vars });
    };

    return {
      t,
      tn,
      ready: current || (!needsLanguage && !needsOverride),
      language: info.language,
      intl: info.intl,
      locale: info.tag
    };
  }, [info, loaded, needsLanguage, needsOverride]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * A translated sentence with one placeholder rendered as an element.
 *
 * "Check {email} for a confirmation link." keeps the address bold and lets a
 * verb-final language put it wherever the sentence needs it. Splitting the
 * sentence into a `before` and an `after` key, the alternative, fixes the
 * English word order into every language.
 */
export function interleave(template: string, name: string, element: React.ReactNode): React.ReactNode {
  const marker = `{${name}}`;
  const at = template.indexOf(marker);
  if (at < 0) return template;
  return (
    <>
      {template.slice(0, at)}
      {element}
      {template.slice(at + marker.length)}
    </>
  );
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n used outside I18nProvider');
  return ctx;
}

export function useT(): I18n['t'] {
  return useI18n().t;
}
